// Nómina — DATOS REALES por cooperativa: empleados, causación de nómina (con prestaciones y
// seguridad social) y pagos, todo a través del motor contable. Antes usaba datos de ejemplo.
// Crear un empleado registra primero su tercero (una nómina lo exige) y luego el contrato.

import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { Add as AddIcon, Badge as BadgeIcon, Paid as PaidIcon } from '@mui/icons-material';
import EncabezadoOperativo from '../components/EncabezadoOperativo';
import FormularioDialog from '../components/FormularioDialog';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useCooperativaOperativa from '../hooks/useCooperativaOperativa';
import { nominaApi, tercerosApi } from '../services/coopnetApi';
import { fecha, fmt, hoy, mesActual, moneda } from '../utils/formato';

const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 2 });

const Nomina = () => {
  const op = useCooperativaOperativa();
  const id = op.cooperativaId;
  const [dlg, setDlg] = useState(null);
  const [ok, setOk] = useState('');
  const [neto, setNeto] = useState({}); // terceroId → último neto causado (sugerencia para el pago)

  const { datos, cargando, error, recargar } = useCargar(async () => {
    if (!id) return null;
    const [empleados, terceros] = await Promise.all([nominaApi.empleados(id), tercerosApi.listar(id, 'Empleado')]);
    return { empleados, terceros };
  }, [id]);

  const empleados = datos?.empleados || [];
  const nombreDe = useMemo(() => new Map((datos?.terceros || []).map((t) => [t.id, t])), [datos]);
  const e = dlg?.empleado;
  const totalSalarios = empleados.reduce((t, x) => t + (x.salarioBasico || 0), 0);

  return (
    <EncabezadoOperativo
      titulo="Nómina"
      subtitulo="Empleados, causación y pagos de nómina"
      operativa={op}
      acciones={
        <>
          <Button variant="outlined" onClick={() => setDlg({ tipo: 'ss' })}>Pagar seguridad social</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlg({ tipo: 'nuevo' })}>Nuevo empleado</Button>
        </>
      }
    >
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {cargando && !datos ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Box sx={{ ...grid(230), mb: 3 }}>
            {[['Empleados', fmt(empleados.length), BadgeIcon, '#6A1B9A'], ['Salarios básicos mensuales', moneda(totalSalarios), PaidIcon, '#0D47A1']].map(([t, v, Icono, color]) => (
              <Card key={t}><CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}1A`, color, display: 'flex' }}><Icono /></Box>
                <Box><Typography variant="h5" fontWeight="bold" sx={{ color }}>{v}</Typography><Typography variant="body2">{t}</Typography></Box>
              </CardContent></Card>
            ))}
          </Box>
          <TablaDatos
            filas={empleados}
            vacio="Aún no hay empleados. Registra el primero con «Nuevo empleado»."
            columnas={[
              { titulo: 'Empleado', render: (x) => nombreDe.get(x.terceroId)?.nombre || x.terceroId },
              { titulo: 'Documento', render: (x) => nombreDe.get(x.terceroId)?.numeroDocumento || '—' },
              { titulo: 'Cargo', render: (x) => x.cargo },
              { titulo: 'Salario básico', align: 'right', render: (x) => moneda(x.salarioBasico) },
              { titulo: 'Aux. transporte', align: 'right', render: (x) => moneda(x.auxilioTransporte) },
              { titulo: 'Ingreso', render: (x) => fecha(x.fechaIngreso) },
              { titulo: 'Acciones', render: (x) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" onClick={() => setDlg({ tipo: 'causar', empleado: x })}>Causar nómina</Button>
                  <Button size="small" onClick={() => setDlg({ tipo: 'pagar', empleado: x })}>Pagar</Button>
                </Box>
              ) },
            ]}
          />
        </>
      )}

      <FormularioDialog
        abierto={dlg?.tipo === 'nuevo'}
        titulo="Nuevo empleado"
        campos={[
          { campo: 'nombre', label: 'Nombre completo', tipo: 'texto', requerido: true },
          { campo: 'tipoDocumento', label: 'Tipo de documento', tipo: 'select', requerido: true, opciones: [{ valor: 'CC', etiqueta: 'Cédula de ciudadanía' }, { valor: 'CE', etiqueta: 'Cédula de extranjería' }, { valor: 'PA', etiqueta: 'Pasaporte' }] },
          { campo: 'numeroDocumento', label: 'Número de documento', tipo: 'texto', requerido: true },
          { campo: 'cargo', label: 'Cargo', tipo: 'texto', requerido: true },
          { campo: 'salarioBasico', label: 'Salario básico mensual', tipo: 'numero', requerido: true, positivo: true },
          { campo: 'auxilioTransporte', label: 'Auxilio de transporte', tipo: 'numero' },
          { campo: 'fechaIngreso', label: 'Fecha de ingreso', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ tipoDocumento: 'CC', fechaIngreso: hoy(), auxilioTransporte: 0 }}
        textoBoton="Registrar empleado"
        onGuardar={async (v) => {
          const tercero = await tercerosApi.crearEmpleado(id, { tipoDocumento: v.tipoDocumento, numeroDocumento: v.numeroDocumento, nombre: v.nombre });
          await nominaApi.crearEmpleado(id, { terceroId: tercero.id, cargo: v.cargo, salarioBasico: v.salarioBasico, auxilioTransporte: v.auxilioTransporte, fechaIngreso: v.fechaIngreso });
          setOk(`Empleado ${v.nombre} registrado.`);
          recargar();
        }}
        onClose={() => setDlg(null)}
      />

      <FormularioDialog
        abierto={dlg?.tipo === 'causar'}
        titulo="Causar nómina"
        descripcion={e ? `${nombreDe.get(e.terceroId)?.nombre || ''} — salario ${moneda(e.salarioBasico)}. Genera el comprobante de causación con prestaciones y aportes.` : ''}
        campos={[
          { campo: 'periodo', label: 'Período (AAAA-MM)', tipo: 'texto', requerido: true },
          { campo: 'fecha', label: 'Fecha del comprobante', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ periodo: mesActual(), fecha: hoy() }}
        textoBoton="Causar"
        onGuardar={async (v) => {
          const r = await nominaApi.causar(id, e.id, { periodo: v.periodo, fecha: v.fecha });
          if (r?.netoPagar != null) setNeto((n) => ({ ...n, [e.terceroId]: r.netoPagar }));
          setOk(r ? `Nómina causada. Neto a pagar: ${moneda(r.netoPagar)} · Seguridad social: ${moneda(r.seguridadSocial)} · Caja de compensación: ${moneda(r.ccf)}.` : 'Nómina causada.');
        }}
        onClose={() => setDlg(null)}
      />

      <FormularioDialog
        abierto={dlg?.tipo === 'pagar'}
        titulo="Pagar nómina"
        descripcion={e ? nombreDe.get(e.terceroId)?.nombre : ''}
        campos={[
          { campo: 'monto', label: 'Monto a pagar', tipo: 'numero', requerido: true, positivo: true, ayuda: e && neto[e.terceroId] ? 'Sugerido: último neto causado' : undefined },
          { campo: 'fecha', label: 'Fecha de pago', tipo: 'fecha', requerido: true },
          { campo: 'medioPagoCaja', label: 'Pagado en efectivo (caja). Si no, por bancos', tipo: 'switch' },
        ]}
        inicial={{ fecha: hoy(), medioPagoCaja: false, monto: e ? neto[e.terceroId] || '' : '' }}
        textoBoton="Registrar pago"
        onGuardar={async (v) => { await nominaApi.pagar(id, { terceroId: e.terceroId, monto: v.monto, fecha: v.fecha, medioPagoCaja: v.medioPagoCaja }); setOk('Pago de nómina registrado.'); }}
        onClose={() => setDlg(null)}
      />

      <FormularioDialog
        abierto={dlg?.tipo === 'ss'}
        titulo="Pagar seguridad social"
        campos={[
          { campo: 'montoSeguridadSocial', label: 'Seguridad social (EPS, pensión, ARL)', tipo: 'numero', requerido: true, positivo: true },
          { campo: 'montoCcf', label: 'Caja de compensación', tipo: 'numero' },
          { campo: 'fecha', label: 'Fecha de pago', tipo: 'fecha', requerido: true },
          { campo: 'medioPagoCaja', label: 'Pagado en efectivo (caja). Si no, por bancos', tipo: 'switch' },
        ]}
        inicial={{ fecha: hoy(), medioPagoCaja: false, montoCcf: 0 }}
        textoBoton="Registrar pago"
        onGuardar={async (v) => { await nominaApi.pagarSeguridadSocial(id, { montoSeguridadSocial: v.montoSeguridadSocial, montoCcf: v.montoCcf, fecha: v.fecha, medioPagoCaja: v.medioPagoCaja }); setOk('Pago de seguridad social registrado.'); }}
        onClose={() => setDlg(null)}
      />
    </EncabezadoOperativo>
  );
};

export default Nomina;
